/**
 * Display-only FX seam. A null rate means the quote is unavailable, and
 * callers MUST fall back to USD display so values are never rendered under a
 * denomination whose rate is unknown.
 */

export interface FxRate {
  readonly rate: number;
  readonly source: "live" | "fallback";
  readonly asOfMs?: number;
}

export type FxRateSource = (base: "USD", quote: string) => FxRate | null;
export type FxRateHydrator = (base: "USD", quote: string) => void;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_BRL_RATE = 5.11;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_EUR_RATE = 0.86;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_CNY_RATE = 6.78;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_HKD_RATE = 7.84;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_KRW_RATE = 1_480;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_INR_RATE = 96.7;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_IDR_RATE = 18_000;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_VND_RATE = 26_300;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_PHP_RATE = 61.6;

/** Bootstrap seed shown only until live hydration lands. */
export const FALLBACK_USD_MXN_RATE = 17.4;

/**
 * Bootstrap seeds for relatively stable fiat quotes. BTC, RUB, NGN, and ARS
 * are deliberately not seeded: a stale volatile or fast-depreciating price
 * is worse than temporarily retaining the USD display while live hydration
 * is pending or unavailable.
 */
const FALLBACK_FX_RATES: Readonly<
  Record<"USD", Readonly<Record<string, number>>>
> = {
  USD: {
    BRL: FALLBACK_USD_BRL_RATE,
    EUR: FALLBACK_USD_EUR_RATE,
    CNY: FALLBACK_USD_CNY_RATE,
    HKD: FALLBACK_USD_HKD_RATE,
    KRW: FALLBACK_USD_KRW_RATE,
    INR: FALLBACK_USD_INR_RATE,
    IDR: FALLBACK_USD_IDR_RATE,
    VND: FALLBACK_USD_VND_RATE,
    PHP: FALLBACK_USD_PHP_RATE,
    MXN: FALLBACK_USD_MXN_RATE,
    USD: 1,
  },
};

let liveFxRateSource: FxRateSource | null = null;
let liveFxRateHydrator: FxRateHydrator | null = null;

type FxRateListener = () => void;

const fxRateListeners = new Set<FxRateListener>();
const fxRateCache = new Map<string, FxRate | null>();

export function notifyFxRateChange(): void {
  fxRateCache.clear();
  for (const listener of fxRateListeners) {
    listener();
  }
}

export function subscribeToFxRateChanges(
  listener: FxRateListener,
): () => void {
  fxRateListeners.add(listener);
  return () => {
    fxRateListeners.delete(listener);
  };
}

export function registerFxRateSource(source: FxRateSource): () => void {
  const previousSource = liveFxRateSource;
  liveFxRateSource = source;
  notifyFxRateChange();

  return () => {
    if (liveFxRateSource === source) {
      liveFxRateSource = previousSource;
      notifyFxRateChange();
    }
  };
}

export function registerFxRateHydrator(hydrator: FxRateHydrator): () => void {
  const previousHydrator = liveFxRateHydrator;
  liveFxRateHydrator = hydrator;

  return () => {
    if (liveFxRateHydrator === hydrator) {
      liveFxRateHydrator = previousHydrator;
    }
  };
}

export function requestFxRateHydration(base: "USD", quote: string): void {
  liveFxRateHydrator?.(base, quote);
}

export function getFxRate(base: "USD", quote: string): FxRate | null {
  const pairId = `${base}:${quote}`;
  if (fxRateCache.has(pairId)) {
    return fxRateCache.get(pairId) ?? null;
  }

  const liveRate = liveFxRateSource?.(base, quote) ?? null;
  if (liveRate) {
    fxRateCache.set(pairId, liveRate);
    return liveRate;
  }

  const rate = FALLBACK_FX_RATES[base][quote];
  const fxRate: FxRate | null =
    rate === undefined ? null : { rate, source: "fallback" };
  fxRateCache.set(pairId, fxRate);
  return fxRate;
}
